import { useEffect, useMemo, useState, type PointerEvent, type ReactNode } from "react";
import { useI18n, type StringKey } from "../i18n";
import { loadContractTypes, loadDashboard, type DashboardData, type TrendPoint } from "../data";
import type { ContractType, Workspace } from "../types";

const pct = (v: number | null) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`);
const num = (v: number | null) => (v === null || v === undefined ? "—" : String(v));
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export type Mode = "12m" | "ytd" | "quarter" | "month";
export interface Period { mode: Mode; year: number; quarter: number; month: string }
const STORE = "peoplecore-dashboard-period";

export function defaultPeriod(store = STORE): Period {
  const now = new Date();
  const base: Period = { mode: "12m", year: now.getFullYear(), quarter: Math.floor(now.getMonth() / 3) + 1, month: iso(now).slice(0, 7) };
  try {
    const saved = JSON.parse(localStorage.getItem(store) ?? "null") as Partial<Period> | null;
    if (saved && ["12m", "ytd", "quarter", "month"].includes(saved.mode as string)) return { ...base, ...saved };
  } catch { /* storage unavailable */ }
  return base;
}

/** The first and last day of the chosen period (the database stops a period at today). */
export function range(p: Period): { from: string; to: string } {
  const now = new Date();
  if (p.mode === "12m") {
    const from = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate() + 1);
    return { from: iso(from), to: iso(now) };
  }
  if (p.mode === "ytd") return { from: `${p.year}-01-01`, to: `${p.year}-12-31` };
  if (p.mode === "quarter") {
    const start = new Date(p.year, (p.quarter - 1) * 3, 1);
    return { from: iso(start), to: iso(new Date(p.year, p.quarter * 3, 0)) };
  }
  const [y, m] = p.month.split("-").map(Number);
  return { from: `${p.month}-01`, to: iso(new Date(y, m, 0)) };
}

/** The period buttons (last 12 months, year to date, quarter, month) shared by the dashboard and analytics. */
export function PeriodControls({ period, setPeriod, shown, busy }: { period: Period; setPeriod: (p: Period) => void; shown?: { from: string; to: string }; busy?: boolean }) {
  const { t, lang } = useI18n();
  const locale = lang === "ar" ? "ar-EG" : "en-GB";
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => thisYear - i);
  const modes: [Mode, StringKey][] = [["12m", "dashLast12"], ["ytd", "dashYtd"], ["quarter", "dashQuarter"], ["month", "dashMonthly"]];
  const fmt = (x: string) => new Date(`${x}T00:00:00`).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });

  return (
    <section className="card period-bar">
      <div className="segmented" role="group" aria-label={t("dashPeriod")}>
        {modes.map(([m, label]) => (
          <button key={m} type="button" aria-pressed={period.mode === m} className={period.mode === m ? "current" : ""}
            onClick={() => setPeriod({ ...period, mode: m })}>{t(label)}</button>
        ))}
      </div>
      <div className="row">
        {(period.mode === "ytd" || period.mode === "quarter") && (
          <select aria-label={t("dashYear")} value={period.year} onChange={(e) => setPeriod({ ...period, year: Number(e.target.value) })}>
            {years.map((y) => <option key={y} value={y}>{period.mode === "ytd" && y !== thisYear ? `${y} · ${t("dashFullYear")}` : y}</option>)}
          </select>
        )}
        {period.mode === "quarter" && (
          <select aria-label={t("dashQuarter")} value={period.quarter} onChange={(e) => setPeriod({ ...period, quarter: Number(e.target.value) })}>
            {[1, 2, 3, 4].filter((q) => period.year < thisYear || (q - 1) * 3 <= new Date().getMonth())
              .map((q) => <option key={q} value={q}>Q{q}</option>)}
          </select>
        )}
        {period.mode === "month" && (
          <input type="month" aria-label={t("dashMonth")} value={period.month} max={iso(new Date()).slice(0, 7)} min={`${thisYear - 5}-01`}
            onChange={(e) => e.target.value && setPeriod({ ...period, month: e.target.value })} />
        )}
        {shown && <span className="muted small-note">{fmt(shown.from)} – {fmt(shown.to)}</span>}
        {busy && <span className="muted small-note">{t("loading")}</span>}
      </div>
    </section>
  );

}

/** Headline HR numbers for a chosen period, the 12-month trend up to its end, and the workforce mix. */
export function Dashboard({ ws }: { ws: Workspace }) {
  const { t, pick, lang } = useI18n();
  const [period, setPeriod] = useState<Period>(defaultPeriod);
  const [d, setD] = useState<DashboardData | null>(null);
  const [busy, setBusy] = useState(false);
  const [contracts, setContracts] = useState<ContractType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [asTable, setAsTable] = useState(false);
  const { from, to } = useMemo(() => range(period), [period]);
  const locale = lang === "ar" ? "ar-EG" : "en-GB";

  useEffect(() => { void loadContractTypes().then(setContracts).catch(() => undefined); }, []);
  useEffect(() => {
    try { localStorage.setItem(STORE, JSON.stringify(period)); } catch { /* storage unavailable */ }
    setBusy(true);
    setError(null);
    loadDashboard(ws.membership.company_id, from, to).then(setD).catch((e) => setError(e.message)).finally(() => setBusy(false));
  }, [ws.membership.company_id, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (x: string) => new Date(`${x}T00:00:00`).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
  const controls = <PeriodControls period={period} setPeriod={setPeriod} shown={d?.period} busy={busy && !!d} />;

  if (!d) return <div className="page">{controls}{error ? <p className="alert danger" role="alert">{error}</p> : <p className="muted">{t("loading")}</p>}</div>;

  const change = d.headcount - d.headcount_start;
  const month = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString(locale, { month: "short" });
  const contractName = (code: string) => { const c = contracts.find((x) => x.code === code); return c ? pick(c.name_en, c.name_ar) : code === "unknown" ? t("dashUnknown") : code; };
  const has = (k: string) => ws.enabled.has(k);
  const short = d.period.days < 300;

  type Tile = { label: StringKey; value: string; sub?: string };
  const movement: Tile[] = [
    { label: "dashHires", value: num(d.hires) },
    { label: "dashLeavers", value: num(d.leavers), sub: `${d.voluntary_leavers} ${t("dashVoluntary")}` },
    { label: "dashTurnoverP", value: pct(d.turnover), sub: short ? `≈ ${pct(d.turnover_annualised)} ${t("dashPerYear")}` : `${t("dashAllLeavers")} ${pct(d.total_turnover)}` },
    { label: "dashEarlyLeavers", value: num(d.early_leavers) },
  ];
  if (has("leave")) movement.push({ label: "dashLeaveDays", value: num(d.leave_days) });
  if (has("attendance")) movement.push({ label: "dashAttendanceP", value: pct(d.attendance_rate), sub: `${t("dashLate")} ${pct(d.late_rate)}` });
  const atEnd: Tile[] = [
    { label: "dashHeadcount", value: num(d.headcount), sub: `${change >= 0 ? "+" : ""}${change} ${t("dashSinceStart")}` },
    { label: "dashTenure", value: d.avg_tenure_years === null ? "—" : `${d.avg_tenure_years} ${t("dashYears")}`, sub: d.avg_age ? `${t("dashAvgAge")} ${d.avg_age}` : undefined },
    { label: "dashProbation", value: num(d.in_probation) },
  ];
  const now: Tile[] = [{ label: "dashStartingSoonT", value: num(d.starting_soon) }];
  if (has("leave")) now.push({ label: "dashOnLeave", value: num(d.on_leave_today), sub: `${d.pending_leave} ${t("dashPendingLeave")}` });
  if (has("hiring_documents")) now.push({ label: "dashDocsOverdue", value: num(d.documents_overdue), sub: t("dashDocsHint") });

  const group = (title: StringKey, tiles: Tile[]) => (
    <section className="kpi-group" aria-label={t(title)}>
      <h2 className="sub-head">{t(title)}</h2>
      <div className="kpis">
        {tiles.map((x) => (
          <div key={x.label} className="stat">
            <span className="stat-label">{t(x.label)}</span>
            <span className="stat-value">{x.value}</span>
            {x.sub && <span className="stat-sub">{x.sub}</span>}
          </div>
        ))}
      </div>
    </section>
  );

  const tenure: [string, StringKey][] = [["lt1", "dashLt1"], ["1to3", "dash1to3"], ["3to5", "dash3to5"], ["5to10", "dash5to10"], ["10plus", "dash10plus"]];
  const age: [string, string][] = [["lt25", "< 25"], ["25to34", "25–34"], ["35to44", "35–44"], ["45to54", "45–54"], ["55plus", "55+"]];

  return (
    <div className={`page${busy ? " updating" : ""}`}>
      {controls}
      {error && <p className="alert danger" role="alert">{error}</p>}
      {d.sample_headcount > 0 && <p className="alert info">{t("dashSampleNote").replace("{n}", String(d.sample_headcount))}</p>}
      {group("dashInPeriod", movement)}
      {group("dashAtEnd", atEnd)}
      {group("dashRightNow", now)}

      <div className="dash-grid">
        <section className="card">
          <div className="card-head">
            <h2>{t("dashHeadcountTrend")}</h2>
            <button type="button" className="link" onClick={() => setAsTable(!asTable)}>{asTable ? t("dashShowChart") : t("dashShowTable")}</button>
          </div>
          <div className="legend"><span><i className="band-swatch" />{t("dashChosenPeriod")}</span></div>
          {asTable ? <TrendTable trend={d.trend} month={month} /> : <LineChart trend={d.trend} month={month} />}
        </section>
        <section className="card">
          <h2>{t("dashHiresLeavers")}</h2>
          <div className="legend"><span><i style={{ background: "var(--chart-1)" }} />{t("dashHires")}</span><span><i style={{ background: "var(--chart-2)" }} />{t("dashLeavers")}</span><span><i className="band-swatch" />{t("dashChosenPeriod")}</span></div>
          {asTable ? <TrendTable trend={d.trend} month={month} /> : <BarChart trend={d.trend} month={month} />}
        </section>
      </div>

      <h2 className="sub-head">{t("dashMixAt").replace("{date}", fmt(d.period.to))}</h2>
      <div className="dash-grid">
        <Breakdown title={t("dashByDepartment")} rows={d.by_department.map((x): [string, number] => [pick(x.name, x.name_ar), x.count])} />
        <Breakdown title={t("dashByTenure")} rows={tenure.map(([k, l]): [string, number] => [t(l), d.by_tenure[k] ?? 0])} />
        <Breakdown title={t("dashByAge")} rows={[...age.map(([k, l]) => [l, d.by_age[k] ?? 0] as [string, number]), ...(d.by_age.unknown ? [[t("dashUnknown"), d.by_age.unknown] as [string, number]] : [])]} />
        <Breakdown title={t("dashByGender")} rows={Object.entries(d.by_gender).map(([k, v]): [string, number] => [k === "female" ? t("female") : k === "male" ? t("male") : t("dashUnknown"), v])} />
        <Breakdown title={t("dashByContract")} rows={Object.entries(d.by_contract).map(([k, v]): [string, number] => [contractName(k), v])} />
        <Breakdown title={t("dashByNationality")} rows={[[t("dashEgyptian"), d.by_nationality.egyptian ?? 0], [t("dashOther"), d.by_nationality.other ?? 0]]} />
      </div>

      {ws.permissions.has("settings.manage") && (
        <section className="card">
          <h2>{t("dashPowerBi")}</h2>
          <p>{t("dashPowerBiText")}</p>
          <a className="btn secondary small" href="https://github.com/akramelaraby/peoplecore/blob/main/docs/power-bi.md" target="_blank" rel="noreferrer">{t("dashPowerBiGuide")}</a>
        </section>
      )}
      <p className="muted small-note">{t("dashAsOf")} <span className="mono">{d.as_of}</span></p>
    </div>
  );
}

export function Breakdown({ title, rows }: { title: string; rows: [string, number][] }) {
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <section className="card">
      <h2>{title}</h2>
      <ul className="hbars">
        {rows.map(([label, n]) => (
          <li key={label}>
            <span>{label}</span>
            <span className="track"><span className="fill" style={{ width: `${(100 * n) / max}%` }} /></span>
            <span className="n">{n}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TrendTable({ trend, month }: { trend: TrendPoint[]; month: (m: string) => string }) {
  const { t } = useI18n();
  return (
    <div className="table-wrap"><table>
      <thead><tr><th>{t("dashMonth")}</th><th>{t("dashHeadcount")}</th><th>{t("dashHires")}</th><th>{t("dashLeavers")}</th></tr></thead>
      <tbody>{trend.map((p) => (
        <tr key={p.month} className={p.in_period ? "strong" : "muted"}><td>{month(p.month)} {p.month.slice(0, 4)}</td><td className="mono">{p.headcount}</td><td className="mono">{p.hires}</td><td className="mono">{p.leavers_voluntary + p.leavers_other}</td></tr>
      ))}</tbody>
    </table></div>
  );
}

const W = 520, H = 200, PAD = { l: 32, r: 8, t: 12, b: 24 };
const niceMax = (v: number) => { if (v <= 4) return 4; const step = Math.pow(10, Math.floor(Math.log10(v))); return Math.ceil(v / step) * step; };

function useHover(n: number) {
  const [i, setI] = useState<number | null>(null);
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * W;
    const k = Math.round(((x - PAD.l) / (W - PAD.l - PAD.r)) * n - 0.5);
    setI(Math.max(0, Math.min(n - 1, k)));
  };
  return { i, onMove, onLeave: () => setI(null) };
}

function Axis({ max, trend, month }: { max: number; trend: TrendPoint[]; month: (m: string) => string }) {
  const ticks = [0, max / 2, max];
  const bw = (W - PAD.l - PAD.r) / trend.length;
  return (
    <g>
      {ticks.map((v) => {
        const y = PAD.t + (H - PAD.t - PAD.b) * (1 - v / max);
        return <g key={v}><line className="grid" x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} /><text x={PAD.l - 6} y={y + 4} textAnchor="end">{Math.round(v)}</text></g>;
      })}
      {trend.map((p, k) => (k % 2 === trend.length % 2 ? null :
        <text key={p.month} x={PAD.l + bw * (k + 0.5)} y={H - 6} textAnchor="middle">{month(p.month)}</text>))}
    </g>
  );
}

/** Shades the months inside the chosen period. */
function Bands({ trend }: { trend: TrendPoint[] }) {
  const bw = (W - PAD.l - PAD.r) / trend.length;
  return (
    <g>
      {trend.map((p, k) => p.in_period && (
        <rect key={p.month} x={PAD.l + bw * k} y={PAD.t} width={bw + 0.5} height={H - PAD.t - PAD.b} className="band" />
      ))}
    </g>
  );
}

function Tip({ x, children }: { x: number; children: ReactNode }) {
  return <div className="chart-tip" style={{ left: `${(x / W) * 100}%` }}>{children}</div>;
}

function LineChart({ trend, month }: { trend: TrendPoint[]; month: (m: string) => string }) {
  const { t } = useI18n();
  const max = niceMax(Math.max(...trend.map((p) => p.headcount), 1));
  const bw = (W - PAD.l - PAD.r) / trend.length;
  const xy = (p: TrendPoint, k: number) => [PAD.l + bw * (k + 0.5), PAD.t + (H - PAD.t - PAD.b) * (1 - p.headcount / max)] as const;
  const path = trend.map((p, k) => `${k ? "L" : "M"}${xy(p, k).join(",")}`).join("");
  const h = useHover(trend.length);
  const last = trend.length - 1;
  return (
    <div className="chart" dir="ltr">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t("dashHeadcountTrend")} onPointerMove={h.onMove} onPointerLeave={h.onLeave}>
        <Bands trend={trend} />
        <Axis max={max} trend={trend} month={month} />
        <path d={path} fill="none" stroke="var(--chart-1)" strokeWidth={2} strokeLinejoin="round" />
        {h.i !== null && <line className="grid" x1={xy(trend[h.i], h.i)[0]} x2={xy(trend[h.i], h.i)[0]} y1={PAD.t} y2={H - PAD.b} />}
        {trend.map((p, k) => (k === last || k === h.i) && (
          <circle key={k} cx={xy(p, k)[0]} cy={xy(p, k)[1]} r={4} fill="var(--chart-1)" stroke="var(--surface-raised)" strokeWidth={2} />
        ))}
        <rect x={PAD.l} y={0} width={W - PAD.l - PAD.r} height={H} fill="transparent" />
      </svg>
      {h.i !== null && <Tip x={xy(trend[h.i], h.i)[0]}><strong>{month(trend[h.i].month)} {trend[h.i].month.slice(0, 4)}</strong>{t("dashHeadcount")}: {trend[h.i].headcount}</Tip>}
    </div>
  );
}

/** A bar with 4px rounded top corners, square at the baseline. */
const bar = (x: number, y: number, w: number, h: number) => {
  if (h <= 0) return "";
  const r = Math.min(4, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
};

function BarChart({ trend, month }: { trend: TrendPoint[]; month: (m: string) => string }) {
  const { t } = useI18n();
  const leavers = (p: TrendPoint) => p.leavers_voluntary + p.leavers_other;
  const max = niceMax(Math.max(1, ...trend.map((p) => Math.max(p.hires, leavers(p)))));
  const bw = (W - PAD.l - PAD.r) / trend.length;
  const plotH = H - PAD.t - PAD.b;
  const w = Math.min(12, (bw - 6) / 2);
  const h = useHover(trend.length);
  return (
    <div className="chart" dir="ltr">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t("dashHiresLeavers")} onPointerMove={h.onMove} onPointerLeave={h.onLeave}>
        <Bands trend={trend} />
        <Axis max={max} trend={trend} month={month} />
        {h.i !== null && <rect x={PAD.l + bw * h.i} y={PAD.t} width={bw} height={plotH} fill="var(--surface-sunken)" />}
        {trend.map((p, k) => {
          const cx = PAD.l + bw * (k + 0.5);
          const hh = (plotH * p.hires) / max, lh = (plotH * leavers(p)) / max;
          return (
            <g key={p.month} opacity={p.in_period ? 1 : 0.35}>
              <path d={bar(cx - w - 1, PAD.t + plotH - hh, w, hh)} fill="var(--chart-1)" />
              <path d={bar(cx + 1, PAD.t + plotH - lh, w, lh)} fill="var(--chart-2)" />
            </g>
          );
        })}
      </svg>
      {h.i !== null && (
        <Tip x={PAD.l + bw * (h.i + 0.5)}>
          <strong>{month(trend[h.i].month)} {trend[h.i].month.slice(0, 4)}</strong>
          {t("dashHires")}: {trend[h.i].hires}<br />
          {t("dashLeavers")}: {leavers(trend[h.i])} ({t("dashVoluntary")} {trend[h.i].leavers_voluntary})
        </Tip>
      )}
    </div>
  );
}
